import '../../styles/table.scss';

import { useQuery } from '@tanstack/react-query';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFilterFnsState,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { useMemo, useState } from 'react';

import { db_ListSurveysRow, Surveys } from '../../api';
import Link from '../govuk/Link';
import {
  baseTableProps,
  defaultMRTConfig,
  listQueryType,
  TableToolbarInternalActions,
  useSyncColumnFilterFns,
} from './TableTypes';

export const listSurveysQuery: listQueryType<db_ListSurveysRow[]> = ({
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  staticFilters,
  globalFilter,
}) => ({
  queryKey: ['surveys', 'list', pageIndex + 1, pageSize, sorting, columnFilters, staticFilters, globalFilter],
  queryFn: async () => {
    columnFilters = columnFilters ?? [];
    if (staticFilters instanceof Array) {
      columnFilters = columnFilters?.concat(staticFilters);
    }
    const parsedFilters = JSON.parse(JSON.stringify(columnFilters));
    const surveys = await Surveys.listSurveys(
      pageIndex + 1,
      pageSize,
      JSON.stringify(sorting),
      JSON.stringify(parsedFilters),
      globalFilter,
    );
    const totalRowCount = surveys && surveys.length ? surveys[0].full_count : 0;
    return {
      data: surveys,
      meta: {
        totalRowCount,
      },
    };
  },
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  retry: 0,
  enabled: pageSize != 100000,
});

export function SurveyTable({ isClientSide, staticFilters }: baseTableProps) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'survey_id', desc: true }]);
  const [columnFilterFns, setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const { data, isError, isLoading, refetch } = useQuery(
    listSurveysQuery({
      pageIndex: isClientSide ? 0 : pagination.pageIndex,
      pageSize: isClientSide ? 10000 : pagination.pageSize,
      sorting: isClientSide ? [] : sorting,
      columnFilters: columnFilters,
      staticFilters: staticFilters,
      globalFilter: isClientSide ? '' : globalFilter,
    }),
  );

  const columns = useMemo<MRT_ColumnDef<db_ListSurveysRow>[]>(
    () => [
      {
        accessorFn: (row) => (
          <Link to={`/surveys/${row.survey_id}/view`} aria-label="view">
            {row.survey_name}
          </Link>
        ),
        id: 'survey_name',
        header: 'Survey name',
        enableColumnFilterModes: false,
      },
      {
        accessorFn: (row) => row.sent_at_formatted,
        id: 'sent_at',
        header: 'Sent on',
        enableColumnFilterModes: false,
        filterVariant: 'date-range',
        sortingFn: 'datetime',
      },
      {
        accessorFn: (row) => row.jurisdictional_response_count,
        id: 'jurisdictional_response_count',
        header: 'Local recipients',
        filterVariant: 'range',
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
      },
      {
        accessorFn: (row) => `${row.created_by_first_name ?? ''} ${row.created_by_last_name ?? ''}`.trim() || 'Not set',
        id: 'created_by',
        header: 'Created by',
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
      },
      {
        accessorFn: (row) => (
          <>
            {row.jurisdictional_submitted_count} / {row.jurisdictional_response_count}
          </>
        ),
        id: 'response_count',
        header: 'Responses',
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
      },
      {
        accessorFn: (row) => `${row.viewed_response_percent ?? 0}%`,
        id: 'viewed_response_percent',
        header: 'Responses viewed %',
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
        enableSorting: false,
      },
      {
        accessorFn: (row) => `${row.processed_response_percent ?? 0}%`,
        id: 'processed_response_percent',
        header: 'Responses processed %',
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
        enableSorting: false,
      },
    ],
    [],
  );

  useSyncColumnFilterFns({ columns, setColumnFilterFns });

  const table = useMaterialReactTable({
    ...defaultMRTConfig,
    columns,
    data: data?.data ?? [],
    manualFiltering: !isClientSide,
    manualPagination: !isClientSide,
    manualSorting: !isClientSide,
    onColumnFiltersChange: (filters) => {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
      setColumnFilters(filters);
    },
    onColumnFilterFnsChange: setColumnFilterFns,
    onGlobalFilterChange: (filter) => {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
      setGlobalFilter(filter);
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: isClientSide ? undefined : data?.meta.totalRowCount,
    state: {
      columnFilters,
      columnFilterFns,
      globalFilter,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      showSkeletons: isLoading,
      sorting,
    },
    muiToolbarAlertBannerProps: isError ? { color: 'error', children: 'Error loading data' } : undefined,
    renderToolbarInternalActions: ({ table }) => <TableToolbarInternalActions table={table} refetch={refetch} />,
  });

  return <MaterialReactTable table={table} />;
}
